import { useState, useEffect } from 'react';
import { DollarSign } from 'lucide-react';

const StepPricing = ({ formData, handleInputChange }) => {
  const [pricingModel, setPricingModel] = useState(formData.pricingModel || 'base'); // 'base' or 'daily'

  // Sync local state with formData when it changes externally (e.g., when loading existing listing)
  useEffect(() => {
    if (formData.pricingModel && formData.pricingModel !== pricingModel) {
      setPricingModel(formData.pricingModel);
    }
  }, [formData.pricingModel]);

  const handlePricingModelChange = (model) => {
    setPricingModel(model);
    handleInputChange('pricingModel', model);
  };

  return (
    <div className="space-y-6">
      {/* Pricing Model Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Pricing Model *
        </label>
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => handlePricingModelChange('base')}
            className={`p-4 rounded-lg border-2 transition-all text-left ${
              pricingModel === 'base'
                ? 'border-emerald-500 bg-emerald-50'
                : 'border-gray-200 hover:border-emerald-300'
            }`}
          >
            <h4 className={`text-sm font-medium ${pricingModel === 'base' ? 'text-emerald-900' : 'text-gray-700'}`}>
              Base Price
            </h4>
            <p className="text-xs text-gray-500 mt-1">Fixed price per person</p>
          </button>
          <button
            type="button"
            onClick={() => handlePricingModelChange('daily')}
            className={`p-4 rounded-lg border-2 transition-all text-left ${
              pricingModel === 'daily'
                ? 'border-emerald-500 bg-emerald-50'
                : 'border-gray-200 hover:border-emerald-300'
            }`}
          >
            <h4 className={`text-sm font-medium ${pricingModel === 'daily' ? 'text-emerald-900' : 'text-gray-700'}`}>
              Daily Price
            </h4>
            <p className="text-xs text-gray-500 mt-1">Price per day/night</p>
          </button>
        </div>
      </div>

      {/* Base Price Option */}
      {pricingModel === 'base' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-emerald-600" />
            Base Price per person ($) *
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            required
            className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
            placeholder="0.00"
            value={formData.basePrice || formData.price || 0}
            onChange={(e) => handleInputChange('basePrice', e.target.value)}
          />
          <p className="mt-1 text-xs text-gray-500">This is the base price. Package-specific pricing can be set in the Packages step.</p>
        </div>
      )}

      {/* Daily Price Option */}
      {pricingModel === 'daily' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-emerald-600" />
            Daily Price per person ($) *
          </label>
          <input
            type="number"
            min="0"
            step="5"
            required
            className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
            placeholder="Enter daily price"
            value={formData.dailyPrice || ''}
            onChange={(e) => {
              const value = e.target.value === '' ? '' : parseFloat(e.target.value) || '';
              handleInputChange('dailyPrice', value);
            }}
          />
          <p className="mt-1 text-xs text-gray-500">
            Price per person per day/night. This will be multiplied by the number of nights selected by clients during booking.
          </p>
        </div>
      )}

      {/* Currency Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Currency
        </label>
        <select
          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
          value={formData.currency || 'USD'}
          onChange={(e) => handleInputChange('currency', e.target.value)}
        >
          <option value="USD">USD ($)</option>
          <option value="EUR">EUR (€)</option>
          <option value="GBP">GBP (£)</option>
          <option value="CAD">CAD ($)</option>
          <option value="AUD">AUD ($)</option>
        </select>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> {pricingModel === 'base' 
            ? 'You can set different pricing for each package in the Packages step. This base price will be used as a default.'
            : 'The daily price will be multiplied by the number of nights selected by clients during booking. Package-specific pricing can override this in the Packages step.'}
        </p>
      </div>
    </div>
  );
};

export default StepPricing;
